import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Flex, Link as ChakraLink, Text } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useForm } from "react-hook-form";

import {
  InputControl,
  SelectControl,
} from "../../components/form/formControls";
import { useCategories } from "../../categorys/calculateServiceFees/detail";
import { formatPriceUS } from "../../utils/utils";
import { attendanceCall } from "../../api/attendance";
import { clientAPI } from "../../api/client";
import { roundNumber0 } from "../../utils/tools";
import Decimal from "decimal.js";

// ─── Decimal helpers ───────────────────────────────────────────────────────
const _D = (v) => {
  try {
    const d = new Decimal(v ?? 0);
    return d.isFinite() ? d : new Decimal(0);
  } catch {
    return new Decimal(0);
  }
};
// ──────────────────────────────────────────────────────────────────────────

const schemaEdit = Yup.object().shape({
  employeeId: Yup.string().required("Nhân viên tham gia là bắt buộc"),
  relativeId: Yup.string().optional(),
  commissionPrice: Yup.string(),
  bonus: Yup.string(),
  amountHeld: Yup.string(),
  amountOldCertificate: Yup.string(),
  commissionAfterCertificateOrDeposit: Yup.string(),
  certificateOrDeposit: Yup.string(),
  accountingFromPreviousPeriodOrAdvance: Yup.string(),
  remainingRevenueAfterAccounting: Yup.string(),
  personalIncomeTax: Yup.string(),
  remainingRevenueAfterTax: Yup.string(),
});

export default memo(function Step3FinalServiceFeeForm({
  lockingId,
  locking,
  item,
  index,
  onChangeRow,
  title,
  handleRemoveRow,
  calcTotalPDV,
}) {
  const fieldKeys = [
    "commissionPrice",
    "bonus",
    "commissionAfterCertificateOrDeposit",
    "certificateOrDeposit",
    "accountingFromPreviousPeriodOrAdvance",
    "remainingRevenueAfterAccounting",
    "personalIncomeTax",
    "remainingRevenueAfterTax",
  ];

  const labelMap = {
    commissionPrice: "PDV trước thuế",
    bonus: "Thưởng khác",
    commissionAfterCertificateOrDeposit: "Tổng PDV trước KT",
    certificateOrDeposit: "Chứng chỉ/ ký quỹ",
    accountingFromPreviousPeriodOrAdvance:
      "KT do đã ĐK kì trước/Đã tạm ứng/KT khác",
    remainingRevenueAfterAccounting: "PDV còn lại sau KT",
    personalIncomeTax: "Thuế TNCN",
    remainingRevenueAfterTax: "PDV còn lại sau thuế",
  };

  const placeholderMap = {
    commissionPrice: "Nhập số tiền",
    bonus: "Nhập thưởng khác",
    commissionAfterCertificateOrDeposit: "Nhập tổng PDV trước KT",
    certificateOrDeposit: "Chứng chỉ/ ký quỹ",
    accountingFromPreviousPeriodOrAdvance:
      "KT do đã DC kỳ trước/Đã tạm ứng/KT khác",
    remainingRevenueAfterAccounting: "PDV còn lại sau KT",
    personalIncomeTax: "Thuế TNCN",
    remainingRevenueAfterTax: "PDV còn lại sau thuế",
  };

  const lockKeys = [
    "commissionAfterCertificateOrDeposit",
    "remainingRevenueAfterAccounting",
    "remainingRevenueAfterTax",
  ];
  const unLockKeys = [
    "commissionPrice",
    "accountingFromPreviousPeriodOrAdvance",
    "personalIncomeTax",
    "certificateOrDeposit",
    "bonus",
  ];
  const {
    register,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schemaEdit),
    defaultValues: { ...item } || {},
  });

  const cleanNumber = (value) => {
    // cho phép 0 hợp lệ
    if (value === 0 || value === "0") return 0;
    return roundNumber0(Number(value?.toString().replace(/,/g, "") || 0));
  };

  const [fields, setFields] = useState(
    fieldKeys.reduce((obj, key) => ({ ...obj, [key]: "" }), {}),
  );

  // ======================
  // Trạng thái 3 mode:
  // - db: mới vào lấy từ DB
  // - manual: user sửa trực tiếp field đó
  // - auto: user sửa field khác làm ảnh hưởng => auto theo công thức
  // ======================
  const certificateModeRef = useRef("db"); // "db" | "manual" | "auto"
  const taxModeRef = useRef("db"); // "db" | "manual" | "auto"

  // để biết user vừa sửa field nào
  const lastChangedRef = useRef(null);

  // chặn vòng lặp khi applyUpdates setValue hàng loạt
  const skipRecalcOnceRef = useRef(false);

  // tránh gọi attendance nhiều lần cho cùng 1 item/employee
  const fetchedAttendanceKeyRef = useRef(null);

  // ======================
  // INIT: mới vào trang -> set đúng DB, KHÔNG tính công thức
  // ======================
  useEffect(() => {
    if (!item) return;

    reset({ ...item });

    setFields((prev) => ({
      ...prev,
      ...Object.fromEntries(
        fieldKeys.map((k) => [k, formatPriceUS(item?.[k] || 0)]),
      ),
    }));

    const commissionPrice = cleanNumber(item?.commissionPrice || 0);
    const bonus = cleanNumber(item?.bonus || 0);
    const commissionAfter = Math.round(commissionPrice + bonus);

    applyUpdates({
      commissionAfterCertificateOrDeposit: commissionAfter,
    });

    // reset mode đúng yêu cầu "mới vào nhận DB"
    certificateModeRef.current = "db";
    taxModeRef.current = "db";
    lastChangedRef.current = null;

    // tránh recalc ngay sau init
    skipRecalcOnceRef.current = true;

    // mới vào trang -> set amountHeld, amountOldCertificate từ attendance (nhưng KHÔNG trigger auto nếu đang db)
    const empId = item?.employeeId;
    if (empId && locking) {
      const key = `${item?._id || ""}_${empId}`;
      if (fetchedAttendanceKeyRef.current !== key) {
        fetchedAttendanceKeyRef.current = key;
        getAttendance(empId, locking);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id]);

  // ======================
  // Công thức
  // ======================
  const CERT_PERCENT = 0.3;
  const CERT_MAX = 30000000;
  const TAX_PERCENT = 0.1;

  // ======================
  // WATCH
  // ======================
  const wCommissionPrice = watch("commissionPrice");
  const wBonus = watch("bonus");
  const wAccounting = watch("accountingFromPreviousPeriodOrAdvance");
  const wCertificate = watch("certificateOrDeposit");
  const wTax = watch("personalIncomeTax");
  const wAmountHeld = watch("amountHeld");
  const wAmountOldCertificate = watch("amountOldCertificate");
  const wEmployeeId = watch("employeeId");

  // ── Trạng thái CCMG: BẮT BUỘC gọi API lấy MỚI ────────────────────────────
  // KHÔNG dùng item.isBrokerDebtCertificate — đó là ảnh chụp lúc tính PDV, có thể
  // đã cũ (NV vừa được bỏ CCMG nhưng form vẫn báo "đã có").
  // null = chưa biết (đang gọi / gọi lỗi) → KHÔNG khoá, để tránh báo nhầm "đã có".
  const [ccmgStatus, setCcmgStatus] = useState(null);
  const [ccmgEmployeeCode, setCcmgEmployeeCode] = useState("");

  useEffect(() => {
    const employeeId = wEmployeeId || item?.employeeId;
    setCcmgStatus(null);
    if (!employeeId) return undefined;

    let cancelled = false;
    clientAPI("get", `/employee/${employeeId}`)
      .then((res) => {
        if (cancelled) return;
        setCcmgStatus(!!res?.isBrokerDebtCertificate);
        setCcmgEmployeeCode(res?.code || "");
      })
      .catch(() => {
        if (!cancelled) setCcmgStatus(null);
      });

    return () => {
      cancelled = true;
    };
  }, [wEmployeeId, item?.employeeId]);

  // Khoá ô "Chứng chỉ/ ký quỹ" khi NV đã có CCMG.
  // Lý do: approveCalculateCommission lọc bỏ NV có cờ này khỏi phần cập nhật
  // BrokerDebtCertificate + BrokerDebtCertificatePeriod, nhưng VẪN ghi
  // IndividualServiceFee(Detail) → sửa tay ở đây sẽ tạo ra "Chi tiết chứng chỉ môi
  // giới" có tiền trong khi "Số tiền tạm giữ" vẫn = 0 và không có lịch sử tạm giữ.
  // Chỉ khoá ở tầng nhập liệu, không đổi logic tính toán bên dưới.
  const isCertLockedByCcmg = (key) =>
    key === "certificateOrDeposit" && ccmgStatus === true;

  const calcAutoCertificate = (
    commissionAfter,
    amountHeld,
    amountOldCertificate,
    isBrokerDebtCertificate,
  ) => {
    if (isBrokerDebtCertificate) return 0;

    // ── Decimal: tính chứng chỉ/ký quỹ tự động ──────────────────────────
    const remaining = _D(CERT_MAX).minus(_D(amountHeld || 0)).plus(_D(amountOldCertificate)).toNumber();
    const maxAllowed = remaining > 0 ? remaining : 0;
    const calc = _D(commissionAfter).mul(_D(CERT_PERCENT)).toNumber();
    // Chỉ làm tròn giá trị tiền tệ cuối (VND)
    return Math.round(Math.min(Math.max(calc, 0), maxAllowed));
    // ────────────────────────────────────────────────────────────────────
  };

  const calcAutoTax = (remainingAfterAccounting) => {
    // ── Decimal: tính thuế TNCN tự động ──────────────────────────────────
    return Math.round(_D(remainingAfterAccounting || 0).mul(_D(TAX_PERCENT)).toNumber());
    // ────────────────────────────────────────────────────────────────────
  };

  const applyUpdates = (updates) => {
    skipRecalcOnceRef.current = true;

    setFields((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(updates).map(([k, v]) => [k, formatPriceUS(v)]),
      ),
    }));

    Object.entries(updates).forEach(([k, v]) => {
      setValue(k, v, { shouldDirty: false, shouldValidate: false });
    });

    onChangeRow(index, updates);
    // calcTotalPDV();
  };

  // ======================
  // Handle change number
  // ======================
  const handleChangeNumberField = useCallback(
    (field) => (event) => {
      const { value } = event.target;
      const numericValue = value.replace(/[^0-9]/g, "");
      let numberValue = Number(numericValue || 0);

      lastChangedRef.current = field;

      if (field === "certificateOrDeposit") {
        certificateModeRef.current = "manual";
        taxModeRef.current = "auto";

        // manual nhập cũng phải clamp <= maxAllowed (nếu không nợ chứng chỉ)
        if (!item?.isBrokerDebtCertificate) {
          const amountHeld = cleanNumber(getValues("amountHeld"));
          const amountOldCertificate = cleanNumber(
            getValues("amountOldCertificate"),
          );
          const remaining =
            CERT_MAX - (amountHeld || 0) + (amountOldCertificate || 0);
          const maxAllowed = remaining > 0 ? remaining : 0;

          // clamp về [0..maxAllowed]
          numberValue = Math.min(Math.max(numberValue, 0), maxAllowed);
          // console.log("handleChangeNumberField", {
          //   numberValue,
          //   amountHeld,
          //   amountOldCertificate,
          // });
        }
      }

      if (field === "personalIncomeTax") {
        taxModeRef.current = "manual";
      }

      if (
        field === "bonus" ||
        field === "commissionPrice" ||
        field === "accountingFromPreviousPeriodOrAdvance"
      ) {
        certificateModeRef.current = "auto";
        taxModeRef.current = "auto";
      }

      setValue(field, numberValue, {
        shouldDirty: true,
        shouldValidate: false,
      });

      setFields((prev) => ({
        ...prev,
        [field]: formatPriceUS(numberValue),
      }));

      onChangeRow(index, { [field]: numberValue });

      triggerRecalc();
    },
    [index, onChangeRow, setValue, item, getValues],
  );

  const triggerRecalc = () => {
    if (skipRecalcOnceRef.current) {
      skipRecalcOnceRef.current = false;
      return;
    }

    const commissionPrice = cleanNumber(getValues("commissionPrice"));
    const bonus = cleanNumber(getValues("bonus"));
    const accounting = cleanNumber(
      getValues("accountingFromPreviousPeriodOrAdvance"),
    );
    const amountHeld = cleanNumber(getValues("amountHeld"));
    const amountOldCertificate = cleanNumber(getValues("amountOldCertificate"));

    // ── Decimal: tính commissionAfter – làm tròn vì VND ──────────────────
    const commissionAfter = Math.round(_D(commissionPrice).plus(_D(bonus)).toNumber());
    const dbCertificate = cleanNumber(item?.certificateOrDeposit || 0);
    const dbTax = cleanNumber(item?.personalIncomeTax || 0);

    let certificateOrDeposit = 0;
    if (certificateModeRef.current === "db") {
      certificateOrDeposit = dbCertificate;
    } else if (certificateModeRef.current === "manual") {
      certificateOrDeposit = cleanNumber(getValues("certificateOrDeposit"));
    } else {
      certificateOrDeposit = calcAutoCertificate(
        commissionAfter,
        amountHeld,
        amountOldCertificate,
        item?.isBrokerDebtCertificate,
      );
    }

    // Decimal: tính PDV còn lại sau KT – làm tròn vì VND
    const remainingRevenueAfterAccounting = Math.round(
      _D(commissionAfter).minus(_D(certificateOrDeposit).plus(_D(accounting))).toNumber(),
    );

    let personalIncomeTax = 0;
    if (taxModeRef.current === "db") {
      personalIncomeTax = dbTax;
    } else if (taxModeRef.current === "manual") {
      personalIncomeTax = cleanNumber(getValues("personalIncomeTax"));
    } else {
      personalIncomeTax = calcAutoTax(remainingRevenueAfterAccounting);
    }

    // Decimal: tính PDV còn lại sau thuế – làm tròn vì VND
    const remainingRevenueAfterTax = Math.round(
      _D(remainingRevenueAfterAccounting).minus(_D(personalIncomeTax)).toNumber(),
    );
    // ────────────────────────────────────────────────────────────────────

    applyUpdates({
      commissionAfterCertificateOrDeposit: commissionAfter,
      remainingRevenueAfterAccounting,
      personalIncomeTax,
      remainingRevenueAfterTax,
    });

    // calcTotalPDV();
  };

  // ======================
  // Recalc theo nguyên tắc "sửa số nào chỉ đổi phía sau"
  // ======================
  useEffect(() => {
    if (!item) return;

    if (skipRecalcOnceRef.current) {
      skipRecalcOnceRef.current = false;
      return;
    }

    const lastChanged = lastChangedRef.current;

    const commissionPrice = cleanNumber(getValues("commissionPrice"));
    const bonus = cleanNumber(getValues("bonus"));
    const accounting = cleanNumber(
      getValues("accountingFromPreviousPeriodOrAdvance"),
    );
    const amountHeld = cleanNumber(getValues("amountHeld"));
    const amountOldCertificate = cleanNumber(getValues("amountOldCertificate"));

    // ── Decimal: tính intermediates – làm tròn VND ───────────────────────
    const commissionAfter = Math.round(_D(commissionPrice).plus(_D(bonus)).toNumber());

    const dbCertificate = cleanNumber(item?.certificateOrDeposit || 0);
    const dbTax = cleanNumber(item?.personalIncomeTax || 0);

    let certificateOrDeposit = 0;
    if (certificateModeRef.current === "db") {
      certificateOrDeposit = dbCertificate;
    } else if (certificateModeRef.current === "manual") {
      certificateOrDeposit = cleanNumber(getValues("certificateOrDeposit"));
    } else {
      certificateOrDeposit = calcAutoCertificate(
        commissionAfter,
        amountHeld,
        amountOldCertificate,
        item?.isBrokerDebtCertificate,
      );
    }

    const remainingRevenueAfterAccounting = Math.round(
      _D(commissionAfter).minus(_D(certificateOrDeposit).plus(_D(accounting))).toNumber(),
    );

    let personalIncomeTax = 0;
    if (taxModeRef.current === "db") {
      personalIncomeTax = dbTax;
    } else if (taxModeRef.current === "manual") {
      personalIncomeTax = cleanNumber(getValues("personalIncomeTax"));
    } else {
      personalIncomeTax = calcAutoTax(remainingRevenueAfterAccounting);
    }

    const remainingRevenueAfterTax = Math.round(
      _D(remainingRevenueAfterAccounting).minus(_D(personalIncomeTax)).toNumber(),
    );
    // ────────────────────────────────────────────────────────────────────

    if (lastChanged === "personalIncomeTax") {
      applyUpdates({
        remainingRevenueAfterTax,
      });
      lastChangedRef.current = null;
      return;
    }

    if (lastChanged === "certificateOrDeposit") {
      const autoTax = calcAutoTax(remainingRevenueAfterAccounting);
      const nextRemainingAfterTax = Math.round(
        remainingRevenueAfterAccounting - autoTax,
      );

      applyUpdates({
        commissionAfterCertificateOrDeposit: commissionAfter,
        remainingRevenueAfterAccounting,
        personalIncomeTax: autoTax,
        remainingRevenueAfterTax: nextRemainingAfterTax,
      });

      lastChangedRef.current = null;
      return;
    }

    if (
      lastChanged === "bonus" ||
      lastChanged === "commissionPrice" ||
      lastChanged === "accountingFromPreviousPeriodOrAdvance"
    ) {
      applyUpdates({
        commissionAfterCertificateOrDeposit: commissionAfter,
        certificateOrDeposit,
        remainingRevenueAfterAccounting,
        personalIncomeTax,
        remainingRevenueAfterTax,
      });

      lastChangedRef.current = null;
      return;
    }

    // ======================
    // CASE D: amountHeld, amountOldCertificate đổi (API) => ảnh hưởng cert => auto
    // ======================
    if (lastChanged == null) {
      if (certificateModeRef.current === "db" && taxModeRef.current === "db") {
        return;
      }

      certificateModeRef.current = "auto";
      taxModeRef.current = "auto";

      const autoCert = calcAutoCertificate(
        commissionAfter,
        amountHeld,
        amountOldCertificate,
        item?.isBrokerDebtCertificate,
      );

      // ── Decimal: tính PDV còn lại sau KT khi amountHeld thay đổi ────────
      const nextRemainingAfterAccounting = Math.round(
        _D(commissionAfter).minus(_D(autoCert).plus(_D(accounting))).toNumber(),
      );

      const nextTax = calcAutoTax(nextRemainingAfterAccounting);
      const nextRemainingAfterTax = Math.round(
        _D(nextRemainingAfterAccounting).minus(_D(nextTax)).toNumber(),
      );
      // ────────────────────────────────────────────────────────────────────

      applyUpdates({
        commissionAfterCertificateOrDeposit: commissionAfter,
        certificateOrDeposit: autoCert,
        remainingRevenueAfterAccounting: nextRemainingAfterAccounting,
        personalIncomeTax: nextTax,
        remainingRevenueAfterTax: nextRemainingAfterTax,
      });
      return;
    }
  }, [
    wCommissionPrice,
    wBonus,
    wAccounting,
    wCertificate,
    wTax,
    wAmountHeld,
    wAmountOldCertificate,
    item?._id,
  ]);

  // ======================
  // Categories + Attendance
  // ======================
  const [shouldFetch, setShouldFetch] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShouldFetch(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  const { constantData } = useCategories(
    watch("employeeId") || item?.employeeId,
    shouldFetch,
  );

  const employeeDatas = useMemo(() => {
    return Object.entries(constantData?.relativeData || {}).map(
      ([key, value]) => ({
        value: key,
        label: value,
      }),
    );
  }, [constantData?.relativeData]);

  const getAttendance = async (employeeId, locking) => {
    try {
      const [certificate, OldCertificate] = await Promise.all([
        attendanceCall.getCertificateByEmployeeId(employeeId),
        attendanceCall.getCertificateOrDepositByLockingIdAndEmployeeId(
          employeeId,
          locking?._id,
        ),
      ]);
      const amountHeld = certificate?.amountHeld || 0;

      const amountOldCertificate = OldCertificate?.certificateOrDeposit || 0;

      // console.log("getAttendance", { amountHeld, amountOldCertificate });

      setValue("amountHeld", Number(amountHeld), {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue("amountOldCertificate", Number(amountOldCertificate), {
        shouldDirty: false,
        shouldValidate: false,
      });
      onChangeRow(index, {
        amountHeld: Number(amountHeld),
        amountOldCertificate: Number(amountOldCertificate),
      });

      if (
        certificateModeRef.current === "auto" ||
        taxModeRef.current === "auto"
      ) {
        lastChangedRef.current = null; // trigger CASE D
      } else {
        lastChangedRef.current = "amountHeld"; // tránh CASE D
      }
    } catch (e) {
      // console.log("getAttendance error", e);
    }
  };

  const onchangeEmpoyeeList = (option) => {
    onChangeRow(index, { employeeId: option });

    if (option && locking) {
      const key = `${item?._id || ""}_${option}`;
      fetchedAttendanceKeyRef.current = key;
      getAttendance(option, locking);
    }
  };

  // ======================
  // Render
  // ======================
  return (
    <Box w={"100%"}>
      <Text
        color={"var(--color-header-employee)"}
        fontSize={"14px"}
        fontWeight={"600"}
      >
        {title ? title : item?.isCollaborator ? "Người bán chính" : "CTV"}
      </Text>

      <Flex
        justifyContent={"space-between"}
        alignItems={"center"}
        mb={"12px"}
        w={"100%"}
        flexWrap={"wrap"}
      >
        {/* Row 1 */}
        <Flex
          justifyContent={"space-between"}
          alignItems={"flex-start"}
          gap={"27px"}
          mb={"12px"}
          w={"100%"}
          flexWrap={"wrap"}
        >
          <Box flex={1}>
            <SelectControl
              label={"Nhân viên tham gia"}
              dataOption={employeeDatas}
              placeholder="Chọn nhân viên tham gia"
              selectedMain={watch("employeeId")}
              onChange={onchangeEmpoyeeList}
              registerProps={register("employeeId")}
              errorMessage={errors.employeeId}
              mwidth="100%"
              isSearchable={true}
              isRequired={true}
            />
          </Box>

          {fieldKeys.slice(0, 3).map((key) => {
            const isDisabled = lockKeys.includes(key);
            return (
              <Box flex={1} key={key}>
                <InputControl
                  label={labelMap[key]}
                  placeholder={placeholderMap[key]}
                  errors={errors[key]}
                  register={register(key)}
                  onChange={handleChangeNumberField(key)}
                  value={fields[key]}
                  mwidth="100%"
                  isLockInput={isDisabled}
                  readOnly={isDisabled}
                />
                {unLockKeys.includes(key) ? (
                  <Text
                    mt={"6px"}
                    color={"var(--color-secondary)"}
                    fontSize={"14px"}
                    fontWeight={400}
                    minW={"140px"}
                  >
                    (Cho phép chỉnh sửa)
                  </Text>
                ) : (
                  <Text
                    mt={"6px"}
                    color={"white"}
                    fontSize={"14px"}
                    fontWeight={400}
                    minW={"140px"}
                  >
                    (Không cho phép chỉnh sửa)
                  </Text>
                )}
              </Box>
            );
          })}
        </Flex>

        {/* Row 2 */}
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={"27px"}
          mb={"12px"}
          w={"100%"}
          flexWrap={"wrap"}
        >
          {fieldKeys.slice(3, 7).map((key) => {
            const isDisabled =
              lockKeys.includes(key) || isCertLockedByCcmg(key);
            return (
              <Box flex={1} key={key}>
                <InputControl
                  label={labelMap[key]}
                  placeholder={placeholderMap[key]}
                  errors={errors[key]}
                  register={register(key)}
                  onChange={handleChangeNumberField(key)}
                  value={fields[key]}
                  mwidth="100%"
                  isLockInput={isDisabled}
                  readOnly={isDisabled}
                />
                {isCertLockedByCcmg(key) ? (
                  <Text
                    mt={"6px"}
                    color={"var(--color-secondary)"}
                    fontSize={"12px"}
                    fontWeight={400}
                    minW={"140px"}
                  >
                    Nhân viên này đã có CCMG nên bạn sẽ không thể sửa số tiền
                    chứng chỉ môi giới, xem chi tiết tại{" "}
                    <ChakraLink
                      href={`/broker-debt-certificate${
                        ccmgEmployeeCode
                          ? `?employeeCode=${encodeURIComponent(
                              ccmgEmployeeCode,
                            )}`
                          : ""
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      textDecoration={"underline"}
                      fontWeight={600}
                    >
                      trang quản lý tạm giữ CCMG
                    </ChakraLink>
                  </Text>
                ) : unLockKeys.includes(key) ? (
                  <Text
                    mt={"6px"}
                    color={"var(--color-secondary)"}
                    fontSize={"14px"}
                    fontWeight={400}
                    minW={"140px"}
                  >
                    (Cho phép chỉnh sửa)
                  </Text>
                ) : (
                  <Text
                    mt={"6px"}
                    color={"white"}
                    fontSize={"14px"}
                    fontWeight={400}
                    minW={"140px"}
                  >
                    (Không cho phép chỉnh sửa)
                  </Text>
                )}
              </Box>
            );
          })}
        </Flex>

        {/* Row 3 */}
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={"27px"}
          mb={"12px"}
          w={"100%"}
          flexWrap={"wrap"}
        >
          {fieldKeys.slice(7, 8).map((key) => {
            const isDisabled = lockKeys.includes(key);
            return (
              <Box flex={1} key={key}>
                <InputControl
                  label={labelMap[key]}
                  placeholder={placeholderMap[key]}
                  errors={errors[key]}
                  register={register(key)}
                  onChange={handleChangeNumberField(key)}
                  value={fields[key]}
                  mwidth="100%"
                  isLockInput={isDisabled}
                  readOnly={isDisabled}
                />
                {unLockKeys.includes(key) && (
                  <Text
                    mt={"6px"}
                    color={"var(--color-secondary)"}
                    fontSize={"14px"}
                    fontWeight={400}
                    minW={"140px"}
                  >
                    (Cho phép chỉnh sửa)
                  </Text>
                )}
              </Box>
            );
          })}
          <Box flex={3}></Box>
        </Flex>
      </Flex>
    </Box>
  );
});
